import React, { useRef, useState, useEffect } from "react";
import { doctorService } from '../services/api';
import { Link } from "react-router-dom";
import StarRating from "./StarRating";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/pagination";
import SwiperCore from "swiper";
import { FreeMode, Autoplay, Navigation } from "swiper/modules";

SwiperCore.use([FreeMode, Autoplay, Navigation]);

const SpecialtyDoctorsCarousel = ({ specialties = [], treatmentTitle = "", currentDoctorIds = [] }) => {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const swiperRef = useRef(null);

  useEffect(() => {
    const fetchDoctors = async () => {
      if (!specialties || specialties.length === 0) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // Fetch all doctors
        const response = await doctorService.getAll({ limit: 1000 });
        const allDoctors = response.data.doctors || [];
        
        // Filter doctors by specialties
        const filteredDoctors = allDoctors.filter(doctor => {
          if (!doctor.specialty) return false;
          
          // Exclude doctors that are already shown on the current page
          const doctorId = doctor.id || doctor._id;
          if (currentDoctorIds.includes(doctorId)) return false;
          
          return specialties.some(treatmentSpec => {
            const doctorSpecialty = doctor.specialty.toLowerCase();
            const treatmentSpecialty = treatmentSpec.toLowerCase();
            
            // Check for exact match or partial match
            return doctorSpecialty.includes(treatmentSpecialty) ||
                   treatmentSpecialty.includes(doctorSpecialty) ||
                   // Handle common variations
                   (treatmentSpecialty.includes('cardio') && doctorSpecialty.includes('cardio')) ||
                   (treatmentSpecialty.includes('neuro') && doctorSpecialty.includes('neuro')) ||
                   (treatmentSpecialty.includes('ortho') && doctorSpecialty.includes('ortho')) ||
                   (treatmentSpecialty.includes('gastro') && doctorSpecialty.includes('gastro')) ||
                   (treatmentSpecialty.includes('oncol') && doctorSpecialty.includes('oncol')) ||
                   (treatmentSpecialty.includes('surgery') && doctorSpecialty.includes('surgeon')) ||
                   (treatmentSpecialty.includes('surgeon') && doctorSpecialty.includes('surgery'));
          });
        });
        
        setDoctors(filteredDoctors);
      } catch (err) {
        console.error('Error fetching doctors:', err);
        setError(err.message);
        setDoctors([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDoctors();
  }, [specialties]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-xl text-blue-600">Loading specialty doctors...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-xl text-red-600">Error loading doctors: {error}</div>
      </div>
    );
  }

  if (doctors.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-xl text-gray-600">No doctors found for this specialty</div>
      </div>
    );
  }

  // Get the primary specialty for the title
  const primarySpecialty = specialties && specialties.length > 0 ? specialties[0] : 'Treatment';

  return (
    <div className="w-full">
      <div className="overflow-hidden relative">
        <Swiper
          ref={swiperRef}
          slidesPerView={1}
          spaceBetween={16}
          loop={doctors.length > 3}
          freeMode={true}
          autoplay={doctors.length > 3 ? { delay: 3000, disableOnInteraction: false } : false}
          speed={1000}
          grabCursor={true}
          navigation={{
            nextEl: '.specialty-swiper-button-next',
            prevEl: '.specialty-swiper-button-prev',
          }}
          breakpoints={{
            640: {
              slidesPerView: 2,
              spaceBetween: 20,
            },
            1024: {
              slidesPerView: 3,
              spaceBetween: 24,
            },
          }}
          className="w-full"
        >
          {doctors.map((doctor, index) => (
            <SwiperSlide key={`${doctor.id || doctor._id}-${index}`}>
              <div className="transition-transform duration-300 ease-in-out transform">
                <Link
                  to={`/doctor-details/${doctor.id || doctor._id}`}
                  className="block"
                >
                  <div className="w-full aspect-[4/3] rounded-lg overflow-hidden">
                    <img 
                      src={doctor.image || doctor.profileImage || 'https://placehold.co/300x400/cccccc/ffffff?text=Doctor'} 
                      alt={doctor.name} 
                      className="w-full h-full object-contain bg-gray-50"
                      onError={(e) => { 
                        e.target.onerror = null; 
                        e.target.src = 'https://placehold.co/300x400/cccccc/ffffff?text=Doctor'; 
                      }}
                    />
                  </div>
                  <div className="mt-3 text-center">
                    <h3
                      className="text-2xl font-semibold text-gray-600 truncate"
                      title={doctor.name}
                    >
                      {doctor.name}
                    </h3>
                    {doctor.rating && (
                      <div className="flex justify-center mt-2">
                        <StarRating 
                          rating={parseFloat(doctor.rating)} 
                          size="small" 
                          showRating={true}
                        />
                      </div>
                    )}
                    <p className="text-md text-black-600 mt-1">
                      {doctor.specialty} | {doctor.experience}
                    </p>
                    <p className="text-md text-black-600 mt-1">
                      {doctor.hospital}
                    </p>
                  </div>
                </Link>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Custom Navigation Buttons */}
        {doctors.length > 1 && (
          <div className="flex justify-center gap-4 mt-10">
            <button
              onClick={() => swiperRef.current?.swiper.slidePrev()}
              aria-label="Previous"
              className="w-12 h-12 rounded-full flex items-center justify-center bg-[#1976f2] hover:bg-blue-700 text-white transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={() => swiperRef.current?.swiper.slideNext()}
              aria-label="Next"
              className="w-12 h-12 rounded-full flex items-center justify-center bg-[#1976f2] hover:bg-blue-700 text-white transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SpecialtyDoctorsCarousel;
